import {
  API_TIMEOUT,
  DEFAULT_RETRY_ATTEMPTS,
  DEFAULT_RETRY_DELAY_MS,
} from '@/constants';
import { getApiErrorMessage, normalizeApiError } from '@/utils/apiError';
import type { RequestOptions } from '@umijs/max';
import { request } from '@umijs/max';
import { message } from 'antd';

type RetryOptions = {
  retries?: number;
  retryDelayMs?: number;
  retryStatusCodes?: number[];
  retryOnNetworkError?: boolean;
};

const API_BASE_URL = process.env.BASE_URL ?? '';

export const withBaseUrl = (path: string) => {
  if (!API_BASE_URL) {
    return path;
  }
  return `${API_BASE_URL.replace(/\/$/, '')}${path}`;
};

type ApiRequestOptions = RequestOptions & {
  retry?: RetryOptions;
  useGlobalErrorHandler?: boolean;
};

type RetryableError = {
  response?: {
    status?: number;
  };
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const shouldRetry = (error: unknown, retry?: RetryOptions) => {
  if (!retry) {
    return false;
  }
  const {
    retryStatusCodes = [429, 502, 503, 504],
    retryOnNetworkError = true,
  } = retry;
  const normalizedError = error as RetryableError;
  const status = normalizedError.response?.status;
  const isNetworkError = !normalizedError.response;
  if (isNetworkError) {
    return retryOnNetworkError;
  }
  return status ? retryStatusCodes.includes(status) : false;
};

export const apiRequest = async <T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> => {
  const { retry, useGlobalErrorHandler = true, ...requestOptions } = options;
  const fullUrl = withBaseUrl(path);
  const retryAttempts = retry?.retries ?? DEFAULT_RETRY_ATTEMPTS;
  const delay = retry?.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

  let attempt = 0;
  let lastError: unknown;

  while (attempt <= retryAttempts) {
    try {
      return await request<T>(fullUrl, {
        ...requestOptions,
        timeout: requestOptions.timeout ?? API_TIMEOUT,
        skipErrorHandler: true,
      });
    } catch (error) {
      lastError = normalizeApiError(error);
      attempt += 1;
      if (attempt > retryAttempts || !shouldRetry(error, retry)) {
        if (useGlobalErrorHandler && typeof window !== 'undefined') {
          message.error(getApiErrorMessage(lastError));
        }
        throw lastError;
      }
      await sleep(delay * attempt);
    }
  }

  if (useGlobalErrorHandler && typeof window !== 'undefined') {
    message.error(getApiErrorMessage(lastError));
  }
  throw normalizeApiError(lastError);
};
